import React from 'react';
import { CalendarDays, ArrowRight, CheckCircle2, Sparkles, Handshake, Rocket } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import { trackCTA } from '../../lib/tracking';

const fases = [
  {
    icon: Handshake,
    title: '1. Conversación inicial',
    text: 'Una reunión breve para conocer al equipo de convivencia y los objetivos del establecimiento.',
  },
  {
    icon: Sparkles,
    title: '2. Parametrización',
    text: 'Configuración del piloto y orientación al equipo para comenzar a registrar observaciones.',
  },
  {
    icon: Rocket,
    title: '3. Marcha blanca',
    text: 'Acompañamiento durante las primeras semanas con soporte y revisión conjunta de resultados.',
  },
];

const incluye = [
  'Licencia SaaS del establecimiento durante el piloto',
  'Implementación inicial guiada',
  'Capacitación al equipo de convivencia',
  'Soporte continuo y mejoras',
  'Reportes preventivos para dirección',
];

const Piloto = () => {
  const a = useReveal();
  const b = useReveal();
  return (
    <section id="piloto" data-testid="piloto-section" className="relative py-14 sm:py-20 overflow-hidden bg-deep text-white">
      <div className="glow-violet" style={{ top: '-180px', left: '-160px', opacity: 0.4 }} aria-hidden />
      <div className="glow-blue" style={{ bottom: '-220px', right: '-200px', opacity: 0.3 }} aria-hidden />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div ref={a.ref} className="reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold uppercase tracking-[0.16em] text-[#C8D8FF]">
            Implementación piloto
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Solicita tu <span className="text-[#78A8FF]">PILOTO</span>.<br />
            Juntos mejoramos la convivencia escolar en Chile.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#C8D8FF] max-w-xl leading-relaxed">
            Únete al ecosistema preventivo de PreventiApp y da el primer paso hacia una convivencia escolar
            más segura, trazable y basada en datos. Acompañamos al colegio en cada etapa.
          </p>

          <ul className="mt-8 space-y-3" data-testid="piloto-incluye-list">
            {incluye.map((i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#78A8FF]/20 text-[#78A8FF] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <span className="text-base text-white/90">{i}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="piloto-cta-reunion"
              onClick={() => { trackCTA('piloto-cta-reunion', { location: 'piloto' }); document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-6 py-4 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 bg-white text-[#0B1B5E] hover:bg-[#EEF4FF] transition-all shadow-[0_18px_36px_-14px_rgba(0,0,0,0.45)]"
            >
              <CalendarDays className="w-5 h-5" />
              Agendar reunión
            </button>
            <button
              data-testid="piloto-cta-form"
              onClick={() => { trackCTA('piloto-cta-form', { location: 'piloto' }); document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-6 py-4 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 transition-all"
            >
              Solicitar piloto
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Fases del piloto */}
        <div ref={b.ref} className="reveal" style={{ transitionDelay: '120ms' }}>
          <div className="space-y-4" data-testid="piloto-fases">
            {fases.map((f, idx) => (
              <div
                key={f.title}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 hover:bg-white/[0.07] transition-all"
                data-testid={`piloto-fase-${idx + 1}`}
              >
                <div className="flex items-start gap-4">
                  <span className="w-12 h-12 rounded-2xl bg-[#78A8FF]/15 text-[#78A8FF] flex items-center justify-center shrink-0 border border-white/10">
                    <f.icon className="w-6 h-6" />
                  </span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{f.title}</h3>
                    <p className="mt-1.5 text-sm text-[#C8D8FF] leading-relaxed">{f.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <p className="text-sm text-[#C8D8FF] uppercase tracking-[0.16em] font-semibold">Resultado esperado</p>
            <p className="mt-3 text-lg text-white font-semibold leading-snug">
              Un equipo de convivencia con información ordenada, alertas oportunas y decisiones respaldadas por evidencia.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Piloto;
